import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toastGetError } from "../../../components/Toaster";
import { PrebuiltCatalogueField } from "../../../utils/prebuiltFields";

/**
 * Prebuilt OAuth / OIDC integration catalogue (Core
 * GET /v2/manage/internal/platform/integrations/oauth).
 *
 * Core returns ONE collection, `FederatedSSOIntegrationsOAuth[]`, of vendor templates.
 * Every template is an OAuth/OIDC template and can serve BOTH protocols — the catalogue
 * does not partition vendors by protocol, so neither does the UI. The catalogue is the
 * source of truth for vendor name, logo, description and form fields; nothing here is
 * mirrored in frontend constants.
 *
 * The catalogue endpoint is NOT feature-gated in Core, so the fetch is gated
 * client-side on `oauth_integration_enabled`, consistent with the rest of the
 * OAuth/OIDC surface.
 */

/**
 * A single configuration field, rendered dynamically. Core uses one catalogue-entry shape for
 * every protocol, so this is an alias of the shared type rather than a separate declaration.
 */
export type OAuthCatalogueField = PrebuiltCatalogueField;

export interface OAuthCatalogueTemplate {
  name: string;
  displayName: string;
  logo?: string;
  description?: string;
  pageTitle?: string;
  fields?: OAuthCatalogueField[];
  /**
   * Marks that this template's seeded defaults include OIDC-specific behaviour (the
   * `openid` scope). It is a defaults hint ONLY — it does not mean the vendor supports
   * OIDC but not OAuth, and it must never be used to classify, label, group or route a
   * template. A created integration's protocol comes from its actual scopes instead.
   * Declared here to describe the Core response faithfully; the UI does not consume it.
   */
  isOIDC?: boolean;
}

export interface OAuthCatalogueState {
  Data: OAuthCatalogueTemplate[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;
}

const initialState: OAuthCatalogueState = {
  Data: [],
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

export const OAuthCatalogueSlice = createSlice({
  name: "oauthCatalogue",
  initialState,
  reducers: {
    getOAuthCatalogueRequest(state) {
      state.isLoading = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
    },
    getOAuthCatalogueSuccess(state, action: PayloadAction<OAuthCatalogueTemplate[]>) {
      state.isLoading = false;
      state.isSuccess = true;
      state.Data = action.payload || [];
    },
    getOAuthCatalogueFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.isError = true;
      state.errorMessage = action.payload;
      // Load failure — suppressed when it is a permission error, as with the integrations list.
      toastGetError(action.payload);
    }
  }
});

export const { getOAuthCatalogueRequest, getOAuthCatalogueSuccess, getOAuthCatalogueFailure } = OAuthCatalogueSlice.actions;

export default OAuthCatalogueSlice.reducer;
